import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/20/solid';
import clsx from 'clsx';
import { LockClosedIcon } from '@heroicons/react/20/solid';
import { fromNullable, just, Maybe, nothing } from 'maybeasy';
import * as React from 'react';
import { Tooltip } from 'react-tooltip';
import { T, TPlainTextKey, translation, TranslationsContext } from '../../../Translations';
import SectionChip from './SectionChip';
import StatusMenu, { StatusAction } from './StatusMenu';
import TierBadge from './TierBadge';
import InlineSpinner from '../../Cutesies/InlineSpinner';
import { CommunicationEntry, CommunicationGroup, PendingAction } from './Types';

interface Props {
  communications: CommunicationEntry[];
  selected: Maybe<number>;
  canManage: boolean;
  busy: boolean;
  pending: Maybe<PendingAction>;
  onSelect: (communicationId: number) => void;
  onToggleOne: Maybe<(communicationId: number, disabled: boolean) => void>;
  onToggleSection: Maybe<(run: GroupRun, disabled: boolean) => void>;
  onRevert: Maybe<(communicationId: number) => void>;
}

export interface GroupRun {
  group: CommunicationGroup;
  communications: CommunicationEntry[];
}

export const GROUP_LABELS: Record<CommunicationGroup, TPlainTextKey> = {
  program_invitations: 'Course invitations',
  coaching_invitations: 'Coaching invitations',
  enrollment_reminders: 'Enrollment reminders',
  getting_started: 'Getting started',
  group_coaching: 'Group coaching',
  during_the_program: 'During the course',
  completion: 'Completion',
  account: 'Account',
  other: 'Other communications',
};

const GROUP_DESCRIPTIONS: Record<CommunicationGroup, TPlainTextKey> = {
  program_invitations:
    'Sent before someone starts: the invitation to register for a course or collection, and the reminders that follow if they have not.',
  coaching_invitations:
    'Invitations to book coaching, and the reminders that follow while no session has been scheduled.',
  enrollment_reminders: 'Nudges for people who registered but have not yet enrolled in a course.',
  getting_started: 'Welcome and orientation messages sent as a course begins.',
  group_coaching: 'Invitations, reminders and cancellations for scheduled group coaching sessions.',
  during_the_program:
    'Triggered by what someone does while learning: live events, coach feedback, chat replies and assignments.',
  completion:
    'Certificates sent at graduation, and the follow-up nudges to enroll in something next.',
  account: 'Account and sign-in messages, sent outside of any one course.',
  other: 'Communications whose trigger has not been placed in a theme yet.',
};

// public_name is present but blank for some communications, so an empty string
// has to fall back to the internal name the same way a missing one does.
const communicationLabel = (communication: CommunicationEntry): string =>
  communication.publicName
    .map((name) => name.trim())
    .andThen((name) => (name === '' ? nothing<string>() : just(name)))
    .getOrElseValue(communication.name);

// The server sends the list already in journey order, so sections are runs of
// consecutive entries rather than something to sort or bucket here.
const groupRuns = (communications: CommunicationEntry[]): GroupRun[] =>
  communications.reduce<GroupRun[]>((runs, communication) => {
    const current = runs[runs.length - 1];
    if (current && current.group === communication.group) {
      current.communications.push(communication);
      return runs;
    }
    return [...runs, { group: communication.group, communications: [communication] }];
  }, []);

const customizedCount = (communications: CommunicationEntry[]): number =>
  communications.filter((communication) => communication.effectiveTier === 'collection').length;

export const disableableIn = (run: GroupRun): CommunicationEntry[] =>
  run.communications.filter((c) => c.disableable);

const offCount = (run: GroupRun): number => run.communications.filter((c) => c.disabled).length;

// A section counts as off when every message in it that CAN be off, is.
const sectionIsOff = (run: GroupRun): boolean => {
  const toggleable = disableableIn(run);
  return toggleable.length > 0 && toggleable.every((c) => c.disabled);
};

const CommunicationsList: React.FC<Props> = ({
  communications,
  selected,
  canManage,
  busy,
  pending,
  onSelect,
  onToggleOne,
  onToggleSection,
  onRevert,
}) => {
  const [toggled, setToggled] = React.useState<Partial<Record<CommunicationGroup, boolean>>>({});
  const translations = React.useContext(TranslationsContext);
  const tooltipId = React.useRef(`communication-groups-${Math.random()}`).current;

  const selectedGroup: Maybe<CommunicationGroup> = selected.andThen((id) => {
    const entry = communications.find((c) => c.communicationId === id);
    return entry ? just(entry.group) : nothing<CommunicationGroup>();
  });

  const isOpen = (group: CommunicationGroup): boolean =>
    fromNullable(toggled[group]).getOrElseValue(
      selectedGroup.map((g) => g === group).getOrElseValue(false),
    );

  const toggle = (group: CommunicationGroup) =>
    setToggled((current) => ({ ...current, [group]: !isOpen(group) }));

  const rowPending = (communicationId: number): boolean =>
    pending
      .map((p) => p.kind === 'row' && p.communicationId === communicationId)
      .getOrElseValue(false);

  const sectionPending = (group: CommunicationGroup): boolean =>
    pending.map((p) => p.kind === 'section' && p.group === group).getOrElseValue(false);

  // While a section is being changed its rows are dimmed, so the work reads as
  // happening to them rather than the page having stopped responding.
  const spinner = <InlineSpinner size={18} />;

  // Only actions that apply right now. An option shown greyed while a message
  // is off could vanish once it is back on -- reverting exists only while there
  // is an override to drop -- so offering it inert promised something the menu
  // could not keep.
  const rowActions = (communication: CommunicationEntry): ReadonlyArray<StatusAction> => {
    if (!canManage) {
      return [];
    }

    // While a message is off, the only thing worth doing to it is turning it
    // back on. Editing copy that cannot be sent, or changing which tier it
    // would inherit from, are both busywork until then.
    if (communication.disabled) {
      return onToggleOne
        .map(
          (toggleOne): ReadonlyArray<StatusAction> => [
            {
              key: 'enable',
              label: 'Turn back on for this collection',
              tone: 'primary',
              closeOnSelect: false,
              onSelect: () => toggleOne(communication.communicationId, false),
            },
          ],
        )
        .getOrElseValue([]);
    }

    const actions: StatusAction[] = [];

    // Opening the editor is navigation, not a change of status -- the chip only
    // moves once something is saved -- so it is named for what it does.
    actions.push({
      key: 'edit',
      label: 'Edit this message',
      tone: 'default',
      closeOnSelect: true,
      onSelect: () => onSelect(communication.communicationId),
    });

    if (communication.effectiveTier === 'collection') {
      onRevert.do((revert) => {
        actions.push({
          key: 'revert',
          label: communication.organizationSubject
            .map(() => 'Use the organization version' as TPlainTextKey)
            .getOrElseValue('Use the default version'),
          tone: 'default',
          closeOnSelect: false,
          onSelect: () => revert(communication.communicationId),
        });
      });
    }

    if (communication.disableable) {
      onToggleOne.do((toggleOne) => {
        actions.push({
          key: 'disable',
          label: 'Turn off for this collection',
          tone: 'muted',
          closeOnSelect: false,
          onSelect: () => toggleOne(communication.communicationId, true),
        });
      });
    }

    return actions;
  };

  return (
    <div>
      <Tooltip
        id={tooltipId}
        place="right"
        variant="light"
        opacity={1}
        border="custom"
        className="rounded-lg z-20 border border-border max-w-xs text-[100%]"
      />
      {groupRuns(communications).map((run) => {
        const open = isOpen(run.group);
        const customized = customizedCount(run.communications);
        const toggleable = disableableIn(run);
        const off = sectionIsOff(run);
        const sectionChip = <SectionChip total={run.communications.length} off={offCount(run)} />;
        return (
          <section key={run.group}>
            {/* The heading is a row, not a button: the accordion toggle and the
                roll-up chip are siblings, because a button cannot nest inside a
                button. */}
            <h2 className="sticky top-0 z-10 flex items-center bg-muted border-b border-border">
              <button
                type="button"
                aria-expanded={open}
                onClick={() => toggle(run.group)}
                data-tooltip-id={tooltipId}
                data-tooltip-content={translation(GROUP_DESCRIPTIONS[run.group])(translations)}
                className="flex-1 min-w-0 flex items-center gap-2 px-3 py-2 text-muted-foreground uppercase tracking-wide hover:brightness-95 transition-all"
                style={{ fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-semibold)' }}
              >
                {open ? (
                  <ChevronDownIcon className="w-4 h-4 shrink-0" />
                ) : (
                  <ChevronRightIcon className="w-4 h-4 shrink-0" />
                )}
                <span className="flex-1 text-left truncate">
                  <T kind={GROUP_LABELS[run.group]} />
                </span>
                {customized > 0 && (
                  <span className="inline-flex items-center px-1.5 rounded-full bg-primary text-primary-foreground normal-case">
                    {customized}
                  </span>
                )}
              </button>
              {/* The roll-up chip is the section control. One element carries
                  the count, the state, and the action. */}
              <div className="pr-2">
                <StatusMenu
                  chip={sectionPending(run.group) ? spinner : sectionChip}
                  disabled={busy}
                  align="right"
                  label={translation('Change how this section is sent')(translations)}
                  intercomTarget="communications-section-status-menu"
                  actions={
                    canManage && toggleable.length > 0
                      ? onToggleSection
                          .map(
                            (toggleSection): ReadonlyArray<StatusAction> => [
                              {
                                key: off ? 'enable-all' : 'disable-all',
                                label: off
                                  ? 'Turn this whole section back on'
                                  : 'Turn this whole section off',
                                tone: off ? 'primary' : 'muted',
                                closeOnSelect: true,
                                onSelect: () => toggleSection(run, !off),
                              },
                            ],
                          )
                          .getOrElseValue([])
                      : []
                  }
                />
              </div>
            </h2>
            {open && (
              <ul
                className={clsx(
                  'divide-y divide-border border-b border-border transition-opacity',
                  sectionPending(run.group) && 'opacity-50',
                )}
              >
                {run.communications.map((communication) => {
                  const isSelected = selected
                    .map((id) => id === communication.communicationId)
                    .getOrElseValue(false);
                  const badge = (
                    <TierBadge
                      tier={communication.effectiveTier}
                      disabled={communication.disabled}
                    />
                  );
                  return (
                    <li
                      key={communication.communicationId}
                      className={clsx(
                        'flex items-start border-l-2 transition-opacity',
                        isSelected
                          ? 'bg-secondary border-primary'
                          : 'border-transparent hover:bg-secondary transition-colors',
                        rowPending(communication.communicationId) && 'opacity-50',
                      )}
                    >
                      <button
                        type="button"
                        className="flex-1 min-w-0 px-4 py-2.5 text-left"
                        onClick={() => onSelect(communication.communicationId)}
                        data-intercom-target="communications-list-item-button"
                      >
                        <span
                          className="text-foreground break-words"
                          style={{
                            fontSize: 'var(--text-sm)',
                            fontWeight: 'var(--font-weight-medium)',
                          }}
                        >
                          {communicationLabel(communication)}
                        </span>
                      </button>
                      {/* The chip opens the menu: status and the actions that
                          change it live in one control, rather than the status
                          sitting here and the actions living in the editor. */}
                      {/* A handful of messages cannot be switched off at all --
                          password resets, and the opening message of each
                          invitation ladder. Saying so beats an inexplicably
                          inert chip. */}
                      {!communication.disableable && (
                        <span
                          className="flex items-center self-stretch pl-1 text-muted-foreground"
                          title={translation('This message cannot be turned off')(translations)}
                        >
                          <LockClosedIcon className="w-3.5 h-3.5" />
                          <span className="sr-only">
                            <T kind="This message cannot be turned off" />
                          </span>
                        </span>
                      )}
                      <div className="pr-3">
                        <StatusMenu
                          chip={rowPending(communication.communicationId) ? spinner : badge}
                          disabled={busy}
                          align="right"
                          label={translation('Change how this message is sent')(translations)}
                          intercomTarget="communications-row-status-menu"
                          actions={rowActions(communication)}
                        />
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        );
      })}
    </div>
  );
};

export default CommunicationsList;
